import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { BookOpen, Search, ChevronDown, ChevronRight, Loader2, Volume2, List } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

const SECTIONS = [
  { label: 'Preamble', range: [0, 0], color: 'slate' },
  { label: 'Foundations & Core Architecture', range: [1, 21], color: 'cyan' },
  { label: 'GPT Framework & Memory', range: [22, 33], color: 'violet' },
  { label: 'Inter-Agent Protocols', range: [34, 54], color: 'amber' },
  { label: 'Backup & Recovery', range: [55, 83], color: 'emerald' },
  { label: 'Memory & Ring Operations', range: [84, 106], color: 'rose' },
  { label: 'Advanced Protocols', range: [107, 154], color: 'cyan' },
  { label: 'Databases & Knowledge', range: [155, 200], color: 'violet' },
  { label: 'Scientific Foundations', range: [201, 255], color: 'amber' },
  { label: 'Human Factors', range: [256, 300], color: 'emerald' },
  { label: 'Engineering & Stewardship', range: [301, 424], color: 'rose' },
  { label: 'Sacred Directives & Cathedral', range: [425, 500], color: 'cyan' },
  { label: 'Advanced Systems & Continuity', range: [501, 533], color: 'violet' },
];

export default function FederationManual({ apiUrl }) {
  const [chapters, setChapters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState({});
  const [activeSection, setActiveSection] = useState(null);
  const [showToc, setShowToc] = useState(true);

  const fetchChapters = useCallback(async () => {
    try {
      const res = await axios.get(`${apiUrl}/documents`, {
        params: { category: 'HumanCodex Chapter', page_size: 600, include_private: true }
      });
      const docs = (res.data.documents || []).map(d => {
        const match = d.title?.match(/Chapter\s+(\d+)/);
        const num = match ? parseInt(match[1]) : 0;
        const section = SECTIONS.find(s => num >= s.range[0] && num <= s.range[1]);
        return { ...d, chapter_number: num, chapter_section: section?.label || 'Other' };
      }).sort((a, b) => a.chapter_number - b.chapter_number);
      setChapters(docs);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [apiUrl]);

  useEffect(() => { fetchChapters(); }, [fetchChapters]);

  const filtered = chapters.filter(ch => {
    const matchesSearch = !search || ch.title?.toLowerCase().includes(search.toLowerCase()) || ch.content_text?.toLowerCase().includes(search.toLowerCase());
    const num = ch.chapter_number || 0;
    const matchesSection = !activeSection || (num >= activeSection.range[0] && num <= activeSection.range[1]);
    return matchesSearch && matchesSection;
  });

  const toggleChapter = (id) => setExpanded(p => ({ ...p, [id]: !p[id] }));

  const readAloud = (text) => {
    if (!window.speechSynthesis) return;
    if (window.speechSynthesis.speaking) { window.speechSynthesis.cancel(); return; }
    const utt = new SpeechSynthesisUtterance(text.slice(0, 5000));
    utt.rate = 1.0;
    window.speechSynthesis.speak(utt);
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200" data-testid="federation-manual">
      <div className="max-w-5xl mx-auto px-4 py-8">

        {/* Header */}
        <div className="text-center mb-6">
          <BookOpen className="w-8 h-8 text-cyan-400 mx-auto mb-3" />
          <h1 className="text-2xl sm:text-3xl font-black text-white mb-2">
            The Immutable <span className="text-cyan-400">HumanCodex</span> NextXus
          </h1>
          <p className="text-sm text-slate-400">Federation Framework · Operational Protocols · Ethical Foundations</p>
          <p className="text-[10px] text-slate-600 mt-1">{chapters.length} Chapters · 2026 Release · Roger Keyserling</p>
          <a href="/manual/live"
            className="inline-flex items-center gap-1.5 mt-3 px-4 py-1.5 rounded-full bg-emerald-950/40 border border-emerald-700/40 text-xs text-emerald-400 hover:bg-emerald-900/40 hover:border-emerald-500/60 transition-all"
            data-testid="live-manual-link">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
            Read the complete Live Manual — synced from the master document →
          </a>
        </div>

        {/* Search */}
        <div className="flex gap-2 mb-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <Input placeholder="Search chapters..." value={search} onChange={e => setSearch(e.target.value)}
              className="pl-9 bg-slate-900/60 border-slate-800/40 text-sm h-9" data-testid="manual-search" />
          </div>
          <Button size="sm" variant="outline" onClick={() => setShowToc(!showToc)} className="h-9 border-slate-700 gap-1">
            <List className="w-3.5 h-3.5" /> TOC
          </Button>
          {activeSection && (
            <Button size="sm" variant="outline" onClick={() => setActiveSection(null)} className="h-9 border-slate-700 text-xs">
              Clear Filter
            </Button>
          )}
        </div>

        {/* Section Navigation (Table of Contents) */}
        {showToc && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 mb-6">
            {SECTIONS.map(sec => {
              const count = chapters.filter(ch => {
                const n = ch.chapter_number || 0;
                return n >= sec.range[0] && n <= sec.range[1];
              }).length;
              const isActive = activeSection?.label === sec.label;
              return (
                <button
                  key={sec.label}
                  onClick={() => setActiveSection(isActive ? null : sec)}
                  className={`text-left p-2.5 rounded-lg border transition-all ${
                    isActive
                      ? 'bg-cyan-900/20 border-cyan-700/40 text-cyan-400'
                      : 'bg-slate-900/40 border-slate-800/30 text-slate-400 hover:border-slate-700/50 hover:text-white'
                  }`}
                  data-testid={`section-${sec.range[0]}`}
                >
                  <div className="text-[10px] text-slate-600 mb-0.5">Ch {sec.range[0]}–{sec.range[1]}</div>
                  <div className="text-xs font-medium leading-snug">{sec.label}</div>
                  <div className="text-[9px] text-slate-600 mt-0.5">{count} chapters</div>
                </button>
              );
            })}
          </div>
        )}

        {/* Stats */}
        <div className="flex items-center gap-4 mb-4 text-[10px] text-slate-600">
          <span>{filtered.length} of {chapters.length} chapters</span>
          {activeSection && <Badge className="bg-cyan-900/20 text-cyan-400 border-cyan-700/30 text-[9px]">{activeSection.label}</Badge>}
        </div>

        {/* Chapters */}
        {loading ? (
          <div className="text-center py-20"><Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-500" /></div>
        ) : (
          <div className="space-y-1">
            {filtered.map(ch => {
              const isOpen = expanded[ch.id];
              const content = ch.content_text || ch.description || '';
              return (
                <div key={ch.id} className="border border-slate-800/30 rounded-lg overflow-hidden" data-testid={`ch-${ch.chapter_number}`}>
                  <button onClick={() => toggleChapter(ch.id)}
                    className="w-full flex items-center gap-2 p-3 text-left hover:bg-slate-900/40 transition-colors">
                    {isOpen ? <ChevronDown className="w-4 h-4 text-cyan-500 flex-shrink-0" /> : <ChevronRight className="w-4 h-4 text-slate-600 flex-shrink-0" />}
                    <span className="text-[10px] text-slate-600 w-8 flex-shrink-0">#{ch.chapter_number || 0}</span>
                    <span className={`text-sm font-medium flex-1 ${isOpen ? 'text-cyan-400' : 'text-slate-300'}`}>{ch.title}</span>
                    <span className="text-[9px] text-slate-700 flex-shrink-0">{ch.chapter_section}</span>
                  </button>
                  {isOpen && (
                    <div className="px-4 pb-4 pt-1 border-t border-slate-800/20">
                      <div className="flex justify-end mb-2">
                        <Button size="sm" variant="ghost" onClick={() => readAloud(ch.title + '. ' + content)}
                          className="text-[10px] text-slate-500 hover:text-cyan-400 h-6 gap-1">
                          <Volume2 className="w-3 h-3" /> Read Aloud
                        </Button>
                      </div>
                      <div className="text-xs text-slate-400 leading-relaxed whitespace-pre-wrap">
                        {content.replace(/^#.*\n\n?/, '').trim() || '(Content pending)'}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
